import { alpha, SxProps } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { type ComponentType, useState } from 'react';
import type { SecuredActionType } from './components/ActionType';
import ResponsiveActions from './components/ResponsiveActions';
import GalleryIntialsComponent from './GalleryIntialsComponent';
import { Icons } from './icons/Icons';
import { C, H } from './Layout';
import { useStyling } from './Theme';

type GalleryCardProps = {
    id: number | string;
    item: any;
    ItemRenderer?: ComponentType<any>;
    onDetails?: Function;
    selectedId?: string;
    imageUrl?: string;
    handleSvgComponent?: Function;
    securedActions?: SecuredActionType[];
    containerSx?: SxProps;
    itemName?: string;
};

export default function GalleryCard({
    id,
    item,
    ItemRenderer,
    onDetails = () => {},
    imageUrl = '',
    handleSvgComponent = undefined,
    securedActions = [],
    selectedId = '',
    containerSx = undefined,
    itemName = ''
}: GalleryCardProps) {
    const { theme, isDarkMode } = useStyling();

    const Icon = typeof handleSvgComponent === 'function' ? handleSvgComponent(item) : Icons.BuildCircleOutlined;
    const [isHover, setIsHover] = useState(false);

    return (
        <C
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            sx={{
                position: 'relative',
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${selectedId === item?.id ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider}`,
                ...containerSx
            }}
        >
            {!isEmpty(securedActions) && (
                <ResponsiveActions
                    securedActions={securedActions}
                    isActivated={isHover}
                    showBorder
                    containerSx={{ position: 'absolute', top: '8px', right: '16px', zIndex: 2 }}
                />
            )}
            <H
                sx={{
                    height: '125px',
                    alignItems: 'stretch',
                    backgroundColor: theme.palette.background.paper,
                    flex: 1,
                    p: 0,
                    '&:hover': {
                        backgroundColor: theme.palette.grey[isDarkMode ? 700 : 100],
                        cursor: 'pointer'
                    }
                }}
                onClick={() => onDetails?.(item.id, item)}
            >
                <H fill sx={{ alignItems: 'flex-start' }}>
                    {handleSvgComponent || imageUrl ? (
                        <H
                            fill
                            sx={{
                                width: '85px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor:
                                    selectedId === item?.id
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : theme.palette.background.default
                            }}
                        >
                            {imageUrl ? (
                                <img src={imageUrl} style={{ height: '100%' }} alt={'Garllery Item'} />
                            ) : (
                                <Icon
                                    sx={{
                                        height: '35px',
                                        width: '40px',
                                        color: theme.palette.grey[600]
                                    }}
                                />
                            )}
                        </H>
                    ) : itemName ? (
                        <GalleryIntialsComponent itemName={itemName} />
                    ) : (
                        <></>
                    )}
                    <ItemRenderer key={id} item={item} />
                </H>
            </H>
        </C>
    );
}
